import type { Status } from "@components/Cards/RewardCard";
import { RewardStatus } from "@components/Cards/RewardStatus";
import ImageWithFallback from "@components/Common/ImageWithFallback";
import Skeleton from "@components/Common/Skeleton";
import StyledPagination from "@components/Common/StyledPagination";
import Table from "@components/Common/Table";
import { useLazyGetHistoryQuery } from "@configs/apis/GetHistory";
import { RewardType } from "@configs/models/constants";
import { getRewardType } from "@configs/utils/utility";
import HistoryBoldIcon from "@icons/HistoryBold";
import dayjs from "dayjs";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const HistoryPage = () => {
  const { t: translate } = useTranslation("translation");

  const headers = [
    translate("challenge"),
    translate("table_header_reward_type"),
    translate("table_header_status"),
    translate("table_header_end_date"),
    "",
  ];

  const [page, setPage] = useState(1);
  const [copiedRewardValue, setCopiedRewardValue] = useState("");
  const itemsPerPage = 5;

  const [
    getHistory,
    { data: historyData, isError, isLoading, isFetching },
  ] = useLazyGetHistoryQuery();

  const fetchHistory = useCallback(async () => {
    await getHistory({
      page,
      itemsPerPage,
    });
  }, [page, itemsPerPage, getHistory]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const HistoryContent = () => {
    // Show skeleton while loading initial data
    if (isLoading) {
      return (
        <main className="mt-7 flex flex-col gap-4 px-5">
          {Array.from({ length: 3 }).map((_, idx) => (
            <Skeleton className="!h-14 w-full rounded" hasContent={false} key={idx} />
          ))}
        </main>
      );
    }

    // Show error or empty state
    if (isError || !historyData?.data || historyData.data.length === 0) {
      return (
        <main className="flex min-h-[400px] flex-col items-center justify-center text-center">
          <HistoryBoldIcon className="text-secondTextColor/50" />
          <p className="mb-2 text-secondTextColor">{translate("no_history_title")}</p>
          <p className="mb-8 text-secondTextColor">{translate("your_rewards_will_appears_after_completed_your_challenge")}</p>
        </main>
      );
    }

    // Show data
    return (
      <main className="mt-7 flex flex-col gap-4 px-5">
        <div className="relative overflow-x-auto rounded-lg shadow-md">
          {/* Loading overlay when fetching new page data */}
          {isFetching && !isLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-activeColor/5">
              <div className="size-10 animate-spin rounded-full border-4 border-secondTextColor/20 border-t-activeColor" />
            </div>
          )}
          <Table className="bg-activeColor/10 text-sm" headers={headers}>
            <tbody>
              {historyData?.data?.map((entry) => {
                const allRewards = entry.rewards;
                return allRewards?.map((reward) => {
                  return (
                    <tr
                      className="min-h-36 border-b text-center"
                      key={reward.id}
                    >
                      <td className="px-6 py-4 font-medium">
                        <div className="flex flex-col items-center gap-4 md:flex-row">
                          <ImageWithFallback
                            alt={reward.name}
                            className="size-20 rounded-xl"
                            src={entry.image}
                          />
                          <span> {entry.challengeName} </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {getRewardType(translate, reward.rewardType, reward.configValue)}
                      </td>
                      <td className="px-6 py-4">
                        <RewardStatus status={reward.status as Status} />
                      </td>
                      <td className="px-6 py-4">
                        {dayjs(reward.expirationDate).format("D/M/YYYY")}
                      </td>
                      <td className="px-6 py-4">
                        {reward.rewardType === RewardType.VOUCHER && (
                          <button
                            className="w-24 rounded bg-buttonColor px-4 py-2 text-sm text-variantTextColor"
                            onClick={() => {
                              navigator.clipboard.writeText(reward.rewardValue);
                              setCopiedRewardValue(reward.rewardValue);
                              setTimeout(() => setCopiedRewardValue(""), 2000);
                            }}
                          >
                            {copiedRewardValue === reward.rewardValue
                              ? translate("voucher_copied")
                              : translate("copy")}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                });
              })}
            </tbody>
            <tfoot>
              <tr className="bg-secondTextColor/10">
                <td className="px-6 py-2 text-center font-medium" colSpan={5}>
                  <div className="flex justify-center">
                    <StyledPagination
                      current={page}
                      onChange={(newPage) => setPage(newPage)}
                      pageSize={itemsPerPage}
                      total={historyData?.count || 0}
                    />
                  </div>
                </td>
              </tr>
            </tfoot>
          </Table>
        </div>
      </main>
    );
  };

  return (
    <>
      <header className="flex w-full justify-between bg-mainColor py-4">
        <div className="flex items-center">
          <h1 className="text-center text-4xl font-semibold">
            {translate("rewards")}
          </h1>
        </div>
      </header>

      <HistoryContent />
    </>
  );
};

export default HistoryPage;
